import React, { useMemo, useState } from "react";
import OutlinedInput from "@mui/material/OutlinedInput";
import InputAdornment from "@mui/material/InputAdornment";
import { outlinedInputSx } from "lib/muiStyle/muiStyle";
import Label from "components/Input/Label/Label";
import HelperMessage from "components/Input/HelperMessage/HelperMessage";
import c from "lib/style/colorVar.scss";
import { Button } from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";

const OutlinedText = ({
  inputRef,
  label,
  subLabel,
  hideLabel = false,
  emptyLabel = false,
  mandatory,
  placeholder,
  errorMessage,
  noErrorMessage,
  onEnterDownFn,
  onchangeFn,
  onfocusFn,
  onblurFn,
  value,
  defaultValue,
  sxParams = {},
  disabled,
  multiline,
  rows,
  readOnly,
  endAdornment,
  startAdornment,
  startAttachment,
  endAttachment,
  divStyle,
  id,
  percentage,
  type,
  numberRange = [],
  autoFocus = false,
}) => {
  const { textFocus, textError } = sxParams;
  const [numberMin, numberMax, step] = numberRange;
  const isPasswordInput = type === "password";

  const [focus, setFocus] = useState(false);
  const [passwordVisible, setPasswordVisible] = useState(false);

  const textInputType = useMemo(() => {
    if (isPasswordInput) {
      return passwordVisible ? "text" : "password";
    }
    return type ?? "text";
  }, [type, passwordVisible])

  const inputId = useMemo(() => {
    if (typeof id === "string") return id;
    if (typeof label === "string") return label;
    return undefined;
  }, [id, label]);

  const onKeyDownHandler = (e) => {
    if (e.key === "Enter") {
      onEnterDownFn && onEnterDownFn(e);
    }
  };

  return (
    <div style={divStyle}>
      {!hideLabel && <Label label={label} subLabel={subLabel} mandatory={mandatory} empty={emptyLabel} />}
      <div style={{ display: "flex", flexDirection: "row", gap: "8px" }}>
        {startAttachment}
        <OutlinedInput
          inputRef={inputRef}
          style={disabled ? { background: c.disabledFieldBg } : {}}
          size={"small"}
          id={inputId}
          value={value}
          type={textInputType}
          placeholder={placeholder}
          defaultValue={defaultValue}
          onKeyDown={onKeyDownHandler}
          onChange={onchangeFn}
          onFocus={((...args) => {
            setFocus(true)
            onfocusFn && onfocusFn(...args);
          })}
          onBlur={((...args) => {
            setFocus(false)
            onblurFn && onblurFn(...args);
          })}
          sx={() => outlinedInputSx(textFocus ?? (readOnly ? false : focus), textError, readOnly)}
          disabled={disabled}
          multiline={multiline}
          rows={rows ?? (multiline ? 5 : 1)}
          autoFocus={autoFocus}
          endAdornment={
            percentage ? (
              <InputAdornment position="end">
                <span style={{ color: c.textSecondary }}>%</span>
              </InputAdornment>
            ) : isPasswordInput ? (
              <InputAdornment position="end">
                <Button
                  variant="adornment-button"
                  disabled={disabled}
                  onClick={() => setPasswordVisible(!passwordVisible)}
                >
                  {passwordVisible ? (
                    <Visibility
                      sx={{ fontSize: "18px", color: "#666" }}
                    />
                  ) : (
                    <VisibilityOff
                      sx={{ fontSize: "18px", color: "#666" }}
                    />
                  )}
                </Button>
              </InputAdornment>
            ) : (
              endAdornment
            )
          }
          startAdornment={startAdornment}
          slotProps={{
            input: {
              readOnly: readOnly,
            },
          }}
          inputProps={{
            ...type === "number" && { min: numberMin, max: numberMax, step: step || "any" },
          }}
        />
        {endAttachment}
      </div>
      {!noErrorMessage && <HelperMessage message={textError && errorMessage} />}
    </div>
  );
};

export default React.memo(OutlinedText);
